import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarBlank, DownloadSimple, Printer } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import api, { daysAgoLocal, formatErr, fmtDate, todayLocal } from "@/lib/api";
import { useTranslation } from "react-i18next";

export function MarkAttendance({ optIn = true, showExport = true }) {
  const { t } = useTranslation();
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [date, setDate] = useState(todayLocal());
  const [students, setStudents] = useState([]);
  const [marks, setMarks] = useState({});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [channels, setChannels] = useState({ send_email: false, send_whatsapp: false, send_sms: false });
  const [reportFrom, setReportFrom] = useState(daysAgoLocal(30));
  const [reportTo, setReportTo] = useState(todayLocal());
  const [rosterSearch, setRosterSearch] = useState("");

  useEffect(() => {
    api
      .get("/teacher/classes", { params: { date } })
      .then((r) => {
        setClasses(r.data);
        setClassId((prev) => (r.data.some((c) => c.id === prev) ? prev : r.data[0]?.id || ""));
      })
      .catch((e) => toast.error(formatErr(e)));
  }, [date]);

  useEffect(() => {
    if (!classId) return;
    api
      .get("/teacher/attendance", { params: { class_id: classId, date } })
      .then((r) => {
        setStudents(r.data.students);
        const m = {};
        r.data.students.forEach((s) => (m[s.id] = s.status));
        setMarks(m);
        setDirty(false);
      })
      .catch((e) => toast.error(formatErr(e)));
  }, [classId, date]);

  const setAll = (status) => {
    const m = {};
    students.forEach((s) => (m[s.id] = status));
    setMarks(m);
    setDirty(true);
  };
  const allPresent = students.length > 0 && students.every((s) => marks[s.id] === "present");

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/teacher/attendance", {
        class_id: classId,
        date,
        marks: Object.entries(marks).map(([student_id, status]) => ({ student_id, status })),
        ...channels,
      });
      const a = data.alerts;
      toast.success(
        `Attendance saved — ${data.absent} absent` +
          (a ? ` · alerts: ${a.email_sent} email, ${a.whatsapp_sent} WhatsApp, ${a.sms_sent || 0} SMS sent` : data.absent ? ` · ${data.absent} in-app absence alert${data.absent > 1 ? "s" : ""} sent` : "")
      );
      setDirty(false);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  const absentCount = Object.values(marks).filter((s) => s === "absent").length;

  const downloadReport = async (fmt) => {
    try {
      const res = await api.get("/teacher/attendance/export", {
        params: { from: reportFrom, to: reportTo, class_id: classId, format: fmt },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `attendance_${reportFrom}_to_${reportTo}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatErr(e, "Export failed"));
    }
  };

  const printReport = () => {
    window.open(`/print/attendance?from=${reportFrom}&to=${reportTo}&class_id=${classId}`, "_blank");
  };

  return (
    <>
      <div className="sticky top-[57px] md:top-0 z-10 bg-[#F4F5F7]/90 backdrop-blur-xl py-3 -mx-4 px-4 md:mx-0 md:px-0">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-2 overflow-x-auto scrollbar-none">
            {classes.map((c) => (
              <button
                key={c.id}
                data-testid={`attendance-class-${c.id}`}
                onClick={() => setClassId(c.id)}
                className={`px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-colors duration-200 ${
                  classId === c.id ? "bg-brand-coral text-white" : "bg-white border border-slate-200 text-slate-600"
                }`}
              >
                {c.name}
                {c.temp_access && (
                  <span data-testid={`temp-badge-${c.id}`} className="ml-1.5 text-[9px] font-extrabold uppercase tracking-wide bg-status-amber/30 text-amber-700 px-1.5 py-0.5 rounded-full">
                    Sub
                  </span>
                )}
              </button>
            ))}
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" data-testid="attendance-date-picker" className="rounded-full font-bold border-slate-300 bg-white">
                <CalendarBlank size={16} className="mr-2" /> {fmtDate(date)}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 rounded-2xl" align="start">
              <Calendar
                mode="single"
                selected={new Date(date + "T00:00:00")}
                onSelect={(d) => d && setDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`)}
              />
            </PopoverContent>
          </Popover>
          <div className="flex-1" />
          <Button
            data-testid="submit-attendance-btn"
            onClick={save}
            disabled={saving || !dirty}
            className="rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold px-6 transition-transform duration-300 active:scale-95"
          >
            {saving ? t("teacherMarkAttendance.submitting", { defaultValue: "Submitting…" }) : dirty ? t("teacherMarkAttendance.submitAttendance", { count: absentCount, defaultValue: `Submit Attendance (${absentCount} absent)` }) : t("teacherMarkAttendance.submitted", { defaultValue: "Submitted" })}
          </Button>
        </div>
      </div>

      <p className="text-sm text-slate-500 mt-4 mb-3" data-testid="attendance-date-label"><span className="font-bold text-slate-700">{fmtDate(date)}</span> · {t("teacherMarkAttendance.checkedMeans", { defaultValue: "Checked = " })}<span className="font-bold text-status-green">{t("teacherMarkAttendance.presentLower", { defaultValue: "present" })}</span>. {t("teacherMarkAttendance.uncheckInstruction", { defaultValue: "Uncheck a student to mark absent, then submit." })}</p>
      {students.some((s) => s.birthday_today) && (
        <div data-testid="birthday-banner" className="mb-3 bg-brand-yellow/15 border border-brand-yellow/40 rounded-2xl px-4 py-3 flex items-center gap-2.5">
          <span className="text-lg">🎂</span>
          <p className="text-sm font-semibold text-amber-800">
            {students
              .filter((s) => s.birthday_today)
              .map((s) => `${s.name} turns ${s.turns} today`)
              .join(" · ")}
            ! Celebrate in class.
          </p>
        </div>
      )}
      <div className="mb-3 max-w-xs">
        <Input data-testid="roster-search-input" placeholder={t("teacherMarkAttendance.searchStudent", { defaultValue: "Search student…" })} value={rosterSearch} onChange={(e) => setRosterSearch(e.target.value)} className="bg-white" />
      </div>
      <div data-hide-ask-edujac="true" className="bg-white border border-slate-200 rounded-2xl overflow-x-auto overscroll-x-contain">
        <table className="w-full min-w-[520px] md:min-w-0 text-sm">
          <thead className="bg-[#F4F5F7] text-left text-xs text-slate-500 uppercase tracking-wide">
            <tr>
              <th className="px-3 md:px-5 py-3 w-10 md:w-12">
                <Checkbox
                  data-testid="select-all-attendance"
                  checked={allPresent}
                  onCheckedChange={(c) => setAll(c ? "present" : "absent")}
                  aria-label={t("teacherMarkAttendance.selectAllStudents", { defaultValue: "Select all students" })}
                />
              </th>
              <th className="px-3 md:px-5 py-3">{t("teacherMarkAttendance.student", { defaultValue: "Student" })}</th>
              <th className="px-3 md:px-5 py-3 whitespace-nowrap">{t("teacherMarkAttendance.roll", { defaultValue: "Roll" })}</th>
              <th className="px-3 md:px-5 py-3 text-right whitespace-nowrap">{t("teacherMarkAttendance.status", { defaultValue: "Status" })}</th>
            </tr>
          </thead>
          <tbody>
            {students.filter((s) => s.name.toLowerCase().includes(rosterSearch.toLowerCase())).map((s) => {
              const present = marks[s.id] === "present";
              return (
                <tr key={s.id} data-testid={`attendance-row-${s.id}`} className={`border-t border-slate-100 transition-colors duration-200 ${present ? "" : "bg-status-red/5"}`}>
                  <td className="px-3 md:px-5 py-3.5">
                    <Checkbox
                      data-testid={`attendance-checkbox-${s.id}`}
                      checked={present}
                      onCheckedChange={(c) => {
                        setMarks((prev) => ({ ...prev, [s.id]: c ? "present" : "absent" }));
                        setDirty(true);
                      }}
                      aria-label={t("teacherMarkAttendance.markStudent", { name: s.name, defaultValue: `Mark ${s.name}` })}
                    />
                  </td>
                  <td className="px-3 md:px-5 py-3.5">
                    <div className="flex items-center gap-2 md:gap-3 min-w-0">
                      <div className={`w-9 h-9 rounded-full grid place-items-center font-heading font-bold text-xs ${present ? "bg-brand-mint/15 text-teal-700" : "bg-status-red/20 text-status-red"}`}>
                        {s.name.split(" ").map((w) => w[0]).join("").slice(0, 2)}
                      </div>
                      <span className="font-semibold leading-tight break-words max-w-[150px] md:max-w-none">{s.name}</span>
                    </div>
                  </td>
                  <td className="px-3 md:px-5 py-3.5 text-slate-500 whitespace-nowrap">{s.roll_no || "—"}</td>
                  <td className="px-3 md:px-5 py-3.5 text-right whitespace-nowrap">
                    <span className={`text-xs font-extrabold px-3 py-1 rounded-full uppercase tracking-wide ${present ? "bg-status-green/15 text-status-green" : "bg-status-red text-white"}`}>
                      {present ? t("teacherMarkAttendance.present", { defaultValue: "Present" }) : t("teacherMarkAttendance.absent", { defaultValue: "Absent" })}
                    </span>
                    {s.marked_at && <p className="text-[10px] text-slate-400 mt-1">{t("teacherMarkAttendance.marked", { defaultValue: "marked" })} {fmtDate(s.marked_at)}</p>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {students.length === 0 && <p className="text-sm text-slate-400 py-10 text-center">{t("teacherMarkAttendance.noStudents", { defaultValue: "No students in this class yet." })}</p>}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-6 bg-white border border-slate-200 rounded-2xl px-5 py-4">
        <span className="text-sm font-semibold text-slate-600">{t("teacherMarkAttendance.notifyParentsVia", { defaultValue: "Also notify parents via:" })}</span>
        <label className="flex items-center gap-2.5 text-sm font-medium cursor-pointer">
          <Checkbox data-testid="attendance-email-checkbox" checked={channels.send_email} onCheckedChange={(c) => setChannels({ ...channels, send_email: !!c })} />
          {t("teacherMarkAttendance.sendEmail", { defaultValue: "Send via Email" })}
        </label>
        <label className="flex items-center gap-2.5 text-sm font-medium cursor-pointer">
          <Checkbox data-testid="attendance-whatsapp-checkbox" checked={channels.send_whatsapp} onCheckedChange={(c) => setChannels({ ...channels, send_whatsapp: !!c })} />
          {t("teacherMarkAttendance.sendWhatsApp", { defaultValue: "Send via WhatsApp" })}
        </label>
        <label className="flex items-center gap-2.5 text-sm font-medium cursor-pointer">
          <Checkbox data-testid="attendance-sms-checkbox" checked={channels.send_sms} onCheckedChange={(c) => setChannels({ ...channels, send_sms: !!c })} />
          {t("teacherMarkAttendance.sendSMS", { defaultValue: "Send via SMS" })}
        </label>
      </div>
      {!optIn && (channels.send_email || channels.send_whatsapp || channels.send_sms) && (
        <p className="mt-3 text-xs font-semibold text-amber-700 bg-status-amber/20 rounded-xl px-4 py-2.5" data-testid="attendance-opt-in-warning">
          {t("teacherMarkAttendance.notificationsDisabled", { defaultValue: "External notifications are disabled for this school. The platform owner must enable the opt-in before Email/WhatsApp/SMS alerts work." })}
        </p>
      )}

      {showExport && (
        <div className="mt-6 bg-white border border-slate-200 rounded-2xl px-5 py-4" data-testid="attendance-report-card">
          <p className="text-sm font-semibold text-slate-600 mb-3">{t("teacherMarkAttendance.attendanceReport", { defaultValue: "Attendance report (up to 3 months)" })}</p>
          <div className="flex flex-wrap items-center gap-3">
            <Input type="date" data-testid="report-from" value={reportFrom} onChange={(e) => setReportFrom(e.target.value)} className="w-40 bg-white" />
            <span className="text-slate-400 text-sm">{t("teacherMarkAttendance.to", { defaultValue: "to" })}</span>
            <Input type="date" data-testid="report-to" value={reportTo} onChange={(e) => setReportTo(e.target.value)} className="w-40 bg-white" />
            <Button variant="outline" data-testid="export-csv-btn" onClick={() => downloadReport("csv")} className="rounded-full font-bold border-slate-300 transition-transform duration-300 active:scale-95">
              <DownloadSimple size={16} className="mr-1.5" /> CSV
            </Button>
            <Button variant="outline" data-testid="export-excel-btn" onClick={() => downloadReport("xlsx")} className="rounded-full font-bold border-slate-300 transition-transform duration-300 active:scale-95">
              <DownloadSimple size={16} className="mr-1.5" /> Excel
            </Button>
            <Button variant="outline" data-testid="print-report-btn" onClick={printReport} className="rounded-full font-bold border-slate-300 transition-transform duration-300 active:scale-95">
              <Printer size={16} className="mr-1.5" /> {t("teacherMarkAttendance.print", { defaultValue: "Print" })}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
